import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import {
  LibraryCollectionDetail,
  LibraryCollectionItem,
  LibraryResourceKind,
  canAddLibraryCollectionMembers,
} from '@digital-twins/shared-types'

import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { libraryCollectionsApi } from '@/lib/api'
import { AudienceCollectionCard } from '@/components/library/AudienceCollectionCard'
import { CollectionFilterBar } from '@/components/library/CollectionFilterBar'
import { collectionSectionId } from '@/components/library/CollectionKpiStrip'
import { CollectionCardLayout } from '@/components/library/CollectionItemCard'
import { CollectionResourceCard } from '@/components/library/CollectionResourceCard'
import { StudyCollectionCard } from '@/components/library/StudyCollectionCard'
import { formatSocialCount } from '@/pages/KnowledgeHubPage/social/socialFormat'

import { CollectionSectionTitle } from './CollectionSectionTitle'
import { collectionCardLayout, collectionSectionGridClass } from './collectionSectionLayout'
import { UseCollectionFiltersResult, useLibraryFacetOptions } from './useCollectionFilters'
import { UseCollectionResourcesResult, useCollectionResources } from './useCollectionResources'

export interface CollectionResourcesAreaProps {
  collection: LibraryCollectionDetail
  /** The page's URL filter state (AF1). */
  collectionFilters: UseCollectionFiltersResult
  /** Opens the add picker on its Resources tab, from the empty state; shown only when `collection.canEdit`. */
  onAdd: () => void
  /** Bumped by the page after a write (a rules edit, a picker change): every section refetches. */
  revision?: number
  /** A study was removed: the page refetches the collection, including its counts. */
  onMembersChanged?: () => void
}

/** Section order (D14). */
export const COLLECTION_RESOURCE_SECTION_ORDER: Array<LibraryResourceKind> = ['study', 'audience', 'dataset', 'document']

/** The kinds that carry facets: the only ones a market, year or brand filter keeps (D14, Rev 9). */
const FACETED_KINDS: ReadonlyArray<LibraryResourceKind> = ['dataset', 'document']

const SKELETON_CARDS_MAX = 4

/** The kinds shown: non-empty ones, and under a facet filter only documents and datasets. */
export function visibleResourceKinds(
  resourceCounts: Record<LibraryResourceKind, number>,
  hasFacetFilters: boolean,
): Array<LibraryResourceKind> {
  return COLLECTION_RESOURCE_SECTION_ORDER.filter(
    (kind) => resourceCounts[kind] > 0 && (!hasFacetFilters || FACETED_KINDS.includes(kind)),
  )
}

/**
 * The `aria-live` count: the unfiltered total with no filter; under a
 * filter the loaded items once every shown section is fully read (the page
 * has no filtered total), else null.
 */
export function resourcesResultCount(
  kinds: Array<LibraryResourceKind>,
  sections: Record<LibraryResourceKind, UseCollectionResourcesResult>,
  resourceCounts: Record<LibraryResourceKind, number>,
  hasFacetFilters: boolean,
): number | null {
  if (!hasFacetFilters) {
    return COLLECTION_RESOURCE_SECTION_ORDER.reduce((sum, kind) => sum + resourceCounts[kind], 0)
  }
  const isSettled = kinds.every((kind) => !sections[kind].isLoading && !sections[kind].hasMore && !sections[kind].hasError)
  if (!isSettled) {
    return null
  }
  return kinds.reduce((sum, kind) => sum + sections[kind].items.length, 0)
}

/** A section read to the end with nothing in it (a filter matched none of that kind): left out. */
function isSectionEmpty(section: UseCollectionResourcesResult): boolean {
  return !section.isLoading && !section.hasError && !section.hasMore && section.items.length === 0
}

function renderCard(
  item: LibraryCollectionItem,
  kind: LibraryResourceKind,
  index: number,
  layout: CollectionCardLayout,
  onRemoveStudy: ((item: LibraryCollectionItem) => void) | null,
  removingToken: string | null,
) {
  if (kind === 'study') {
    return (
      <StudyCollectionCard
        key={item.token}
        item={item}
        index={index}
        layout={layout}
        onRemove={onRemoveStudy ? () => onRemoveStudy(item) : undefined}
        isRemoving={removingToken === item.token}
      />
    )
  }
  if (kind === 'audience') {
    return <AudienceCollectionCard key={item.token} item={item} index={index} layout={layout} />
  }
  return <CollectionResourceCard key={item.token} item={item} index={index} layout={layout} />
}

interface CollectionResourceSectionProps {
  kind: LibraryResourceKind
  label: string
  /** The kind's unfiltered count (sizes the skeleton). */
  count: number
  section: UseCollectionResourcesResult
  onRemoveStudy: ((item: LibraryCollectionItem) => void) | null
  removingToken: string | null
}

/**
 * One kind's paged card grid with "Show more"; hairline skeletons after
 * 150 ms. The grid follows the item count (`collectionSectionGridClass`).
 */
function CollectionResourceSection(props: CollectionResourceSectionProps) {
  const { kind, label, count, section, onRemoveStudy, removingToken } = props

  const { t, i18n } = useTranslation('app')

  const testId = `collection-resources-section-${kind}`

  const onRetry = () => {
    void section.refresh()
  }

  let body
  if (section.isLoading && section.items.length === 0) {
    body = (
      <div
        className={`${collectionSectionGridClass(Math.min(count, SKELETON_CARDS_MAX), 'compact')} animate-in fade-in fill-mode-both delay-150`}
        aria-hidden="true"
        data-testid={`${testId}-loading`}
      >
        {Array.from({ length: Math.max(1, Math.min(count, SKELETON_CARDS_MAX)) }, (_, index) => (
          <Skeleton key={index} className="h-[180px] rounded-2xl border border-card-border bg-background" />
        ))}
      </div>
    )
  } else if (section.hasError && section.items.length === 0) {
    body = (
      <div className="flex items-center gap-3" data-testid={`${testId}-error`}>
        <p className="text-sm text-muted-foreground">{t('library.collections.resources.loadFailed')}</p>
        <Button variant="outline" size="sm" shape="pill" onClick={onRetry} data-testid={`${testId}-retry`}>
          {t('library.collections.tryAgain')}
        </Button>
      </div>
    )
  } else {
    body = (
      <div className={collectionSectionGridClass(section.items.length, 'compact')} data-testid={`${testId}-grid`}>
        {section.items.map((item, index) =>
          renderCard(
            item,
            kind,
            index,
            collectionCardLayout(index, section.items.length, 'compact'),
            onRemoveStudy,
            removingToken,
          ),
        )}
      </div>
    )
  }

  return (
    <section
      id={collectionSectionId(kind)}
      aria-label={label}
      className="flex scroll-mt-20 flex-col gap-6"
      data-testid={testId}
    >
      <CollectionSectionTitle
        title={label}
        count={count > 0 ? formatSocialCount(count, i18n.language) : null}
        testIdPrefix={testId}
      />
      {body}
      {section.hasMore ? (
        <div>
          <Button
            variant="outline"
            size="sm"
            shape="pill"
            disabled={section.isLoadingMore}
            onClick={section.loadMore}
            data-testid={`${testId}-show-more`}
          >
            {t('library.collections.showMore')}
          </Button>
        </div>
      ) : null}
    </section>
  )
}

/**
 * The collection's Resources (D14): the sticky `CollectionFilterBar` (its count shows only under a filter, where it is a
 * result), then one paged `?kind=` section per non-empty kind under the
 * filters, each titled in the page's middle tier. Under a market, year or
 * brand filter only documents and datasets show, with the muted note. A
 * change in the collection's counts or in `revision` (a write elsewhere on
 * the page) refetches the sections: a remount would not, since the cached
 * infinite pages are not revalidated on mount. Adding lives in the page
 * header; the empty state offers it again to a viewer who can edit.
 */
export function CollectionResourcesArea(props: CollectionResourcesAreaProps) {
  const { collection, collectionFilters, onAdd, revision = 0, onMembersChanged } = props

  const { t } = useTranslation('app')
  const [removingToken, setRemovingToken] = useState<string | null>(null)

  const { filters, hasFacetFilters, toggleOption, removeOption, clear } = collectionFilters
  const { resourceCounts } = collection
  const kinds = visibleResourceKinds(resourceCounts, hasFacetFilters)
  const tokenFor = (kind: LibraryResourceKind) => (kinds.includes(kind) ? collection.token : null)

  const { filterOptions } = useLibraryFacetOptions()
  // One read per kind, a fixed hook order; a hidden kind reads nothing.
  const sections: Record<LibraryResourceKind, UseCollectionResourcesResult> = {
    study: useCollectionResources(tokenFor('study'), 'study', filters),
    audience: useCollectionResources(tokenFor('audience'), 'audience', filters),
    dataset: useCollectionResources(tokenFor('dataset'), 'dataset', filters),
    document: useCollectionResources(tokenFor('document'), 'document', filters),
  }

  // Paged sections are not refetched by a write elsewhere (AF1): follow the detail's counts and the page's revision.
  const countsKey = JSON.stringify([revision, ...COLLECTION_RESOURCE_SECTION_ORDER.map((kind) => resourceCounts[kind])])
  const seenCountsKey = useRef(countsKey)
  const sectionsRef = useRef(sections)
  sectionsRef.current = sections
  useEffect(() => {
    if (seenCountsKey.current === countsKey) {
      return
    }
    seenCountsKey.current = countsKey
    for (const kind of COLLECTION_RESOURCE_SECTION_ORDER) {
      void sectionsRef.current[kind].refresh()
    }
  }, [countsKey])

  const kindLabel = (kind: LibraryResourceKind) => t(`library.collections.resources.kinds.${kind}`)

  const totalResources = COLLECTION_RESOURCE_SECTION_ORDER.reduce((sum, kind) => sum + resourceCounts[kind], 0)
  const resultCount = resourcesResultCount(kinds, sections, resourceCounts, hasFacetFilters)
  const isFilteredEmpty = hasFacetFilters && resultCount === 0

  // A saved filter holds rules only (D1): no Add, even for an editor.
  const canEdit = canAddLibraryCollectionMembers(collection)

  const onRemoveStudy = async (item: LibraryCollectionItem) => {
    setRemovingToken(item.token)
    try {
      await libraryCollectionsApi.addMember(collection.token, { kind: 'study', token: item.token, mode: 'exclude' })
      toast.success(t('library.collections.menu.removed', { name: collection.name }))
      onMembersChanged?.()
    } catch {
      toast.error(t('library.collections.menu.updateFailed'))
    } finally {
      setRemovingToken(null)
    }
  }

  if (totalResources === 0 && !hasFacetFilters) {
    return (
      <section
        aria-label={t('library.collections.resources.title')}
        className="flex flex-col gap-4"
        data-testid="collection-resources-area"
      >
        <CollectionSectionTitle title={t('library.collections.resources.title')} testIdPrefix="collection-resources" />
        <div className="flex flex-col items-start gap-3" data-testid="collection-resources-empty">
          <p className="text-sm text-muted-foreground">{t('library.collections.resources.empty')}</p>
          {canEdit ? (
            <Button variant="outline" size="sm" shape="pill" onClick={onAdd} data-testid="collection-resources-empty-add">
              {t('library.collections.add')}
            </Button>
          ) : null}
        </div>
      </section>
    )
  }

  return (
    <section
      aria-label={t('library.collections.resources.title')}
      className="flex flex-col gap-4"
      data-testid="collection-resources-area"
    >
      <CollectionFilterBar
        filters={filters}
        options={filterOptions}
        onToggleOption={toggleOption}
        onRemoveOption={removeOption}
        onClear={clear}
        resultCount={hasFacetFilters ? resultCount : null}
      />
      {hasFacetFilters ? (
        <p className="text-sm text-muted-foreground" data-testid="collection-resources-filter-note">
          {t('library.collections.resources.filters.note')}
        </p>
      ) : null}
      {isFilteredEmpty || (hasFacetFilters && kinds.length === 0) ? (
        <div className="flex flex-col items-start gap-3" data-testid="collection-resources-filtered-empty">
          <p className="text-sm text-muted-foreground">{t('library.collections.resources.filteredEmpty')}</p>
          <Button variant="outline" size="sm" shape="pill" onClick={clear} data-testid="collection-resources-clear">
            {t('library.collections.clear')}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-14 pt-4">
          {kinds.filter((kind) => !isSectionEmpty(sections[kind])).map((kind) => (
            <CollectionResourceSection
              key={kind}
              kind={kind}
              label={kindLabel(kind)}
              count={resourceCounts[kind]}
              section={sections[kind]}
              onRemoveStudy={canEdit ? onRemoveStudy : null}
              removingToken={removingToken}
            />
          ))}
        </div>
      )}
    </section>
  )
}
